import { feathers } from '@/feathers-client';
import { buildQueryParams } from '@/Core/utilities/queryParams';
import actionHeaders from '@/Campaigns/config/table-headers/published-campaign-actions';
import commonActions, { formatActionSchedule } from './common-actions';

const initialState = {
  isLoading: false,
  // only applicable if campaign requires access code
  isAuthorized: false,
  hasError: false,
  isSuccess: false,
  errorMessage: '',
  successMessage: '',
  headers: actionHeaders,
  campaign: {},
  campaigns: [],
  actions: [],
  tags: [],
  totalCampaigns: 0,
  totalActions: 0,
};

export default {
  namespaced: true,
  state: { ...initialState },

  getters: {
    ...commonActions.getters,
    tagsList(state) {
      return state.tags.map(tag => tag.tag);
    },
  },

  mutations: {
    setErrorMessage(state, error) {
      state.hasError = true;
      state.errorMessage = error;
    },
    setSuccessMessage(state, message) {
      state.isSuccess = true;
      state.successMessage = message;
    },
    setIsAuthorized(state, bool) {
      state.isAuthorized = bool;
    },
    setIsLoading(state, bool) {
      state.isLoading = bool;
    },
    setPublishedCampaign(state, campaign) {
      state.campaign = campaign;
    },
    setPublishedCampaigns(state, campaigns) {
      state.campaigns = campaigns;
    },
    setPublishedCampaignActions(state, actions) {
      state.actions = actions;
    },
    setTags(state, tags) {
      state.tags = tags;
    },
    setTotalCampaigns(state, total) {
      state.totalCampaigns = total;
    },
    setTotalCampaignActions(state, total) {
      state.totalActions = total;
    },
    setHasErrorManual(state, hasError) {
      state.hasError = hasError;
    },
    setIsSuccessManual(state, isSuccess) {
      state.isSuccess = isSuccess;
    },
  },

  actions: {
    async findPublishedCampaigns(
      { commit },
      {
        page, descending, sortByField, limit, hideRestrictedCampaigns,
        search = null,
        searchTags = null,
      },
    ) {
      commit('setIsLoading', true);

      const query = buildQueryParams({ page, descending, sortByField, limit });
      if (hideRestrictedCampaigns) query.requires_access_code = false;

      // searchTag is an array of strings
      if (searchTags) query.tags = { $in: searchTags };
      if (search) query['campaign.name'] = { $like: `%${search}%` };

      const response = await feathers.service('published-campaigns').find({ query });

      commit('setPublishedCampaigns', response.data);
      commit('setTotalCampaigns', response.total);
      commit('setIsLoading', false);
    },

    async getOnePublishedCampaign({ commit }, campaignId) {
      const response = await feathers.service('published-campaigns').get(campaignId);
      commit('setPublishedCampaign', response);
    },

    async findPublishedCampaignTags({ commit }, searchTerm = null) {
      const query = { $limit: 50 };
      if (searchTerm) query.tag = { $like: `%${searchTerm}%` };

      const response = await feathers.service('published-campaign-tags').find({ query });
      commit('setTags', response.data);
    },

    async savePublishedCampaign({ commit }, { id, accessCode = null }) {
      commit('setIsLoading', true);
      const params = {};
      if (accessCode) params.access_code = accessCode;

      try {
        const response = await feathers.service(`published-campaigns/${id}/saves`).create(params);
        commit('setIsLoading', false);
        return response;
      } catch (err) {
        commit('setErrorMessage', err.message);
        commit('setIsLoading', false);
      }
    },

    async createPublishedCampaign({ commit }, data) {
      commit('setIsLoading', true);
      try {
        const response = await feathers.service(`published-campaigns`).create(data);
        commit('setIsLoading', false);
        commit('setSuccessMessage', 'Your campaign has been published');
        return response;
      } catch (err) {
        commit('setErrorMessage', err.message);
        commit('setIsLoading', false);
      }
    },

    async findPublishedCampaignActions(
      { commit },
      { page, descending, sortByField, limit, id, accessCode = null },
    ) {
      commit('setIsAuthorized', false);
      commit('setIsLoading', true);
      try {
        const query = buildQueryParams({ page, descending, sortByField, limit });
        if (accessCode) query.access_code = accessCode;

        const response = await feathers.service(`published-campaigns/${id}/actions`).find({
          query,
        });

        const actions = response.data.map(action => {
          formatActionSchedule(action);
          return action;
        });

        commit('setPublishedCampaignActions', actions);
        commit('setTotalCampaignActions', response.total);
        commit('setIsAuthorized', true);
        commit('setIsLoading', false);
      } catch (err) {
        commit('setErrorMessage', err.message);
        commit('setIsLoading', false);
      }
    },
    async removePublishedCampaign({ commit }, id) {
      commit('setIsLoading', true);
      try {
        const response = await feathers.service(`published-campaigns`).remove(id);
        commit('setSuccessMessage', 'Your campaign has been unpublished');
        return response;
      } catch (err) {
        commit('setErrorMessage', err.message);
      } finally {
        commit('setIsLoading', false);
      }
    },
    async updatePublishedCampaign({ commit }, payload) {
      try {
        const { id: publishedCampaignId, ...updatedPublishedCampaign } = payload;
        commit('setIsLoading', true);
        const response = await feathers.service(`published-campaigns`).patch(publishedCampaignId, updatedPublishedCampaign );
        commit('setSuccessMessage', 'Your published campaign has been updated');
        return response;
      } catch (err) {
        commit('setErrorMessage', err.message);
      } finally {
        commit('setIsLoading', false);
      }
    },
    resetMessageState({ commit }) {
      commit('setHasErrorManual', false);
      commit('setIsSuccessManual', false);
    },
    resetPublishedCampaignState({ commit }) {
      commit('setPublishedCampaign', {})
    }
  },
};
